/* File: breadcrumbs.jsx
 * Path: ananunadm/src/components/main/breadcrumbs.jsx
*/
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import apiClient, * as API from "@/helpers/api";
import config from "@/config";
import { useLang } from "@/helpers/language";
import { IconChevronRight, IconHome, IconSettings } from "@tabler/icons-react";
import {
	ActionIcon,
	Breadcrumbs,
	Anchor,
	Box,
	Loader,
	Menu,
	Skeleton,
	Tooltip,
	useMantineColorScheme
} from "@mantine/core";
import { useMobile } from "@/helpers/is-mobile";
import { useUser } from "@/helpers/userContext";
import { useGlobal } from "@/helpers/global";
import { filterMenuItemsByReadPermission } from "@/helpers/platformMenuAccess";
import {
	getPageSettingsActions,
	withoutPageSettingsItems
} from "@/helpers/pageSettingsNavigation";
import { T } from "@/helpers/translator";
import { tr as formsTr, pickLocale as pickFormLocale } from "@/pages/Forms/formsShared";

const filesBrowserBasePath = "/files/browser";
const filesApiBase = config.api.files?.root || "files";
const filesBrowserSectionLabels = {
	"apit-folders": "All Folders",
	"shared-with-me": "Shared with me",
	"my-files": "My Files",
	recent: "Recent",
	favorites: "Favorites",
	trash: "Trash"
};

function encodeFilesBrowserSubPath(segments = []) {
	return segments
		.filter(Boolean)
		.map((segment) => encodeURIComponent(segment))
		.join("/");
}

function buildFilesBrowserCrumbs(parts = []) {
	return parts.map((part) =>
		typeof part === "string" ? { text: part, to: null } : { text: part.text, to: part.to || null }
	);
}

const pickLocalizedText = (value, lng) => {
	if (value == null) return "";
	if (typeof value === "string") return value;
	if (typeof value === "number") return String(value);
	if (typeof value === "object" && !Array.isArray(value))
	{
		return (
			value[lng] ||
			value.en ||
			value.ru ||
			value.hy ||
			Object.values(value).find((item) => typeof item === "string" && item.trim()) ||
			""
		);
	}
	return "";
};

const pickLocalizedPersonName = (person, lng) => pickLocalizedText(person?.name, lng);

const navigationRoutePath = (route = "") => `/${String(route).split("?")[0].replace(/^\/+/, "")}`;

const navigationLabel = (item, lng) =>
	pickLocalizedText(item?.title || item?.name, lng) || item?.label || "";

function BreadcrumbPill({ label, to, alternatives = [], active = false, home = false, isMobile }) {
	const link = (
		<Anchor
			component={Link}
			to={to}
			aria-label={home ? "Home" : undefined}
			underline="never"
			sx={(theme) => {
				const isDark = theme.colorScheme === "dark";
				const accent = theme.colors[theme.primaryColor][isDark ? 4 : 7];
				const hoverBg = isDark ? "rgba(255, 255, 255, 0.07)" : "rgba(0, 0, 0, 0.04)";
				const activeBg = isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)";
				return {
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					padding: 10,
					borderRadius: 8,
					whiteSpace: "nowrap",
					fontSize: theme.fontSizes.sm,
					fontWeight: active ? 600 : 500,
					color: active
						? accent
						: isDark
							? theme.colors.dark[0]
							: theme.colors.gray[7],
					backgroundColor: active ? activeBg : "transparent",
					boxShadow: "none",
					transition:
						"background-color 200ms ease, color 200ms ease, box-shadow 200ms ease",
					"&:hover": {
						backgroundColor: active ? activeBg : hoverBg,
						boxShadow: "none",
						color: accent
					},
					"&:focus-visible": {
						outline: `2px solid ${theme.colors[theme.primaryColor][5]}`,
						outlineOffset: 2
					}
				};
			}}
		>
			{home ? <IconHome size={19} stroke={1.8} /> : label}
		</Anchor>
	);

	if (!alternatives.length) return link;

	return (
		<Menu
			trigger={isMobile ? "click" : "hover"}
			openDelay={90}
			closeDelay={180}
			position="bottom-start"
			offset={8}
			withinPortal
			shadow="lg"
			styles={(theme) => {
				const isDark = theme.colorScheme === "dark";
				const accent = theme.colors[theme.primaryColor][isDark ? 4 : 7];
				const hoverBg = isDark ? "rgba(255, 255, 255, 0.07)" : "rgba(0, 0, 0, 0.04)";
				return {
					dropdown: {
						padding: theme.spacing.xs,
						borderRadius: 8,
						minWidth: 190,
						maxHeight: "calc(100dvh - 66px - 200px)",
						overflowY: "auto",
						overscrollBehavior: "contain"
					},
					item: {
						borderRadius: 8,
						padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
						fontWeight: 500,
						transition: "background-color 200ms ease, color 200ms ease",
						"& + &": { marginTop: 4 },
						"&:hover": {
							backgroundColor: hoverBg,
							color: accent
						}
					}
				};
			}}
		>
			<Menu.Target>{link}</Menu.Target>
			<Menu.Dropdown>
				{alternatives.map((option) => (
					<Menu.Item key={`${option.to}-${option.label}`} component={Link} to={option.to}>
						{option.label}
					</Menu.Item>
				))}
			</Menu.Dropdown>
		</Menu>
	);
}

function PageSettingsAction({ actions, lang }) {
	if (!actions.length) return null;

	const settingsLabel = T("settings", lang);
	const button = (
		<ActionIcon
			variant="light"
			size="lg"
			aria-label={settingsLabel}
			sx={{ flexShrink: 0 }}
		>
			<IconSettings size={21} stroke={1.8} />
		</ActionIcon>
	);

	if (actions.length === 1)
	{
		return (
			<Tooltip label={navigationLabel(actions[0], lang) || settingsLabel} position="bottom" withinPortal>
				<ActionIcon
					component={Link}
					to={`/${String(actions[0].route).replace(/^\/+/, "")}`}
					variant="light"
					size="lg"
					aria-label={navigationLabel(actions[0], lang) || settingsLabel}
					sx={{ flexShrink: 0 }}
				>
					<IconSettings size={21} stroke={1.8} />
				</ActionIcon>
			</Tooltip>
		);
	}

	return (
		<Menu position="bottom-end" withinPortal shadow="md">
			<Menu.Target>{button}</Menu.Target>
			<Menu.Dropdown>
				<Menu.Label>{settingsLabel}</Menu.Label>
				{actions.map((action) => (
					<Menu.Item
						key={action.id || action.route}
						component={Link}
						to={`/${String(action.route).replace(/^\/+/, "")}`}
						icon={<IconSettings size={16} stroke={1.8} />}
					>
						{navigationLabel(action, lang) || settingsLabel}
					</Menu.Item>
				))}
			</Menu.Dropdown>
		</Menu>
	);
}

export function BreadcrumbsComponent() {
	const location = useLocation();
	const [isBusy, setIsBusy] = useState(true);
	const [crumbs, setCrumbs] = useState([]);
	const { lang } = useLang();
	const { user } = useUser();
	const { platforms } = useGlobal();
	const { colorScheme } = useMantineColorScheme();
	const isMobile = useMobile();

	// Memoized route mapping with proper ticket system mapping
	const routeMap = useMemo(
		() => ({
			// Todo mappings
			todo: config.api.todo?.board,
			todoBoard: config.api.todo?.board,
			todoCategory: config.api.todo?.category,
			todoTask: config.api.todo?.task,
			todoTag: config.api.todo?.tag,
			todoLabel: config.api.todo?.label,

			// Calendar mappings
			calendar: config.api.calendar?.event,
			calendarEvent: config.api.calendar?.event,

			// Office Map mappings
			officeMap: config.api.officeMap?.room,
			"office-map": config.api.officeMap?.room,

			// CRM mappings
			crm: config.api.crm?.contact,
			contact: config.api.crm?.contact,
			contactGroup: config.api.crm?.contactGroup,
			"contact-group": config.api.crm?.contactGroup,

			// E-Commerce mappings
			eCommerce: config.api.ecommerce?.order,
			ecommerce: config.api.ecommerce?.order,
			ec: config.api.ecommerce?.order,
			brand: config.api.ecommerce?.brand,
			category: config.api.ecommerce?.category,
			certificate: config.api.ecommerce?.certificate,
			field: config.api.ecommerce?.field,
			order: config.api.ecommerce?.order,
			product: config.api.ecommerce?.product,
			store: config.api.ecommerce?.store,
			promoCode: config.api.ecommerce?.promoCode,
			"promo-code": config.api.ecommerce?.promoCode,
			sale: config.api.ecommerce?.sale,
			tag: config.api.ecommerce?.tag,

			// HR mappings
			hr: config.api.hr?.user,
			department: config.api.hr?.department,
			position: config.api.hr?.position,
			payroll: config.api.hr?.payroll,
			user: config.api.hr?.user,
			users: config.api.hr?.user,

			// Recruitment mappings
			recruitment: config.api.hr?.recruitmentJobs,
			recruitmentJobs: config.api.hr?.recruitmentJobs,
			recruitmentApplications: config.api.hr?.recruitmentApplications,
			recruitmentInterviews: config.api.hr?.recruitmentInterviews,
			recruitmentScorecards: config.api.hr?.recruitmentScorecards,
			recruitmentAssessments: config.api.hr?.recruitmentAssessments,
			recruitmentAssessmentResults: config.api.hr?.recruitmentAssessmentResults,
			recruitmentAi: config.api.hr?.recruitmentAi,
			recruitmentOffers: config.api.hr?.recruitmentOffers,
			recruitmentOnboarding: config.api.hr?.recruitmentOnboarding,
			recruitmentDashboard: config.api.hr?.recruitmentDashboard,

			// IT mappings
			it: config.api.it?.pc,
			keyboard: config.api.it?.keyboard,
			monitor: config.api.it?.monitor,
			mouse: config.api.it?.mouse,
			pc: config.api.it?.pc,
			printer: config.api.it?.printer,
			router: config.api.it?.router,
			smb: config.api.it?.smb,
			software: config.api.it?.software,
			switch: config.api.it?.switch,
			room: config.api.it?.room || config.api.system?.room,

			// Content Management mappings
			cm: config.api.cm?.blog?.list,
			blog: config.api.cm?.blog?.list,
			blogPage: config.api.cm?.blog?.page,
			events: config.api.cm?.events?.list,
			eventsPage: config.api.cm?.events?.page,
			service: config.api.cm?.service?.list,
			servicePage: config.api.cm?.service?.page,
			about: config.api.cm?.about?.page,
			"about-us": config.api.cm?.about?.page,
			aboutUs: config.api.cm?.about?.page,
			address: config.api.cm?.address?.map,
			contact: config.api.cm?.contact?.page,
			contactPage: config.api.cm?.contact?.page,
			contactPart: config.api.cm?.contact?.part,
			contactSocial: config.api.cm?.contact?.social,
			contactForm: config.api.cm?.contact?.form,
			instagramPage: config.api.cm?.contact?.instagram?.page,
			instagram: config.api.cm?.contact?.instagram?.list,
			map: config.api.cm?.address?.map,
			state: config.api.cm?.address?.state,
			city: config.api.cm?.address?.city,
			zipCode: config.api.cm?.address?.zipCode,
			"zip-code": config.api.cm?.address?.zipCode,
			banner: config.api.cm?.banner,
			slide: config.api.cm?.slide,
			priceList: config.api.cm?.priceList?.page,
			"price-list": config.api.cm?.priceList?.page,
			forBusiness: config.api.cm?.forBusiness?.list,
			"for-business": config.api.cm?.forBusiness?.page,
			forBusinessPage: config.api.cm?.forBusiness?.page,
			forBusinessList: config.api.cm?.forBusiness?.list,
			subscription: config.api.cm?.subscription?.page,
			"subscription-page": config.api.cm?.subscription?.page,
			subscriptionPage: config.api.cm?.subscription?.page,
			faq: config.api.cm?.faq?.page,
			privacyPolicy: config.api.cm?.pages?.privacyPolicy,
			"privacy-policy": config.api.cm?.pages?.privacyPolicy,
			termsAndConditions: config.api.cm?.pages?.termsAndConditions,
			"terms-and-conditions": config.api.cm?.pages?.termsAndConditions,
			cookiePolicy: config.api.cm?.pages?.cookiePolicy,
			"cookie-policy": config.api.cm?.pages?.cookiePolicy,
			certificatePromoCode: config.api.cm?.pages?.certificatePromoCode,
			"certificate-promo-code": config.api.cm?.pages?.certificatePromoCode,
			deliveryAndPayment: config.api.cm?.pages?.deliveryAndPayment,
			"delivery-and-payment": config.api.cm?.pages?.deliveryAndPayment,
			whoWeAre: config.api.cm?.about?.whoWeAre,
			"who-we-are": config.api.cm?.about?.whoWeAre,
			aboutUsItems: config.api.cm?.about?.item,
			ourMission: config.api.cm?.about?.ourMission,
			"our-mission": config.api.cm?.about?.ourMission,

			// Media mappings
			media: config.api.media?.photo,
			album: config.api.media?.album,
			photo: config.api.media?.photo,
			video: config.api.media?.video,

			// Radio mappings
			radio: config.api.radio?.track,
			track: config.api.radio?.track,
			artist: config.api.radio?.artist,
			stream: config.api.radio?.stream,
			playlist: config.api.radio?.playlist,
			advertisement: config.api.radio?.track, // Fallback to track since advertisement route doesn't exist

			// System mappings
			sys: config.api.system?.role,
			system: config.api.system?.role,
			role: config.api.system?.role,
			permission: config.api.system?.permission,
			notification: config.api.system?.notification,
			upload: config.api.system?.upload,
			activityTracker: config.api.system?.activityTracker,

			// Database/Project mappings
			database: config.api.database?.project, // Main database entry point
			project: config.api.database?.project,
			pr: config.api.database?.project,
			branch: config.api.database?.branch,
			community: config.api.database?.community,
			type: config.api.database?.projectType,
			projectType: config.api.database?.projectType,
			village: config.api.database?.village,
			step: config.api.database?.step,
			voltage: config.api.database?.voltage,

			// Ticket system mappings - Fixed the main issue
			ticket: config.api.ticketSystem?.item, // This was missing!
			ticketSystem: config.api.ticketSystem?.item,
			problem: config.api.ticketSystem?.problem,
			status: config.api.ticketSystem?.status,

			// Chat mappings
			chat: config.api.chat?.chatGroup,
			chatGroup: config.api.chat?.chatGroup,
			message: config.api.chat?.message,

			// AI mappings
			ai: config.api.ai?.conversation,
			conversation: config.api.ai?.conversation,
			aiChat: config.api.ai?.chat,
			aiMessage: config.api.ai?.message,

			// Finance mappings
			finance: config.api.finance?.transactions,
			transaction: config.api.finance?.transactions,
			transactions: config.api.finance?.transactions,
			financeTransaction: config.api.finance?.transactions,
			budget: config.api.finance?.budgets,
			budgets: config.api.finance?.budgets,
			goal: config.api.finance?.goals,
			goals: config.api.finance?.goals,
			cardIssuer: config.api.finance?.cardIssuer,
			"card-issuer": config.api.finance?.cardIssuer,
			cardIssuers: config.api.finance?.cardIssuer,
			"card-issuers": config.api.finance?.cardIssuer,
			card: config.api.finance?.cards,
			cards: config.api.finance?.cards,
			account: config.api.finance?.accounts,
			accounts: config.api.finance?.accounts,
			category: config.api.finance?.categories,
			categories: config.api.finance?.categories,

			// Forms mappings
			forms: config.api.forms?.list
		}),
		[]
	);

	// Static routes that don't need API calls
	const staticRoutes = useMemo(
		() =>
			new Set([
				"interview1",
				"interview2",
				"interview3",
				"interview4",
				"archive",
				"contract",
				"all",
				"working",
				"fired",
				"maternityLeave",
				"project-dashboard",
				"project-search",
				"hr-dashboard",
				"it-dashboard",
				"finance-dashboard",
				"analytics",
				"office-map",
				"find-anything",
				"list",
				"page",
				"ads",
				"add",
				"edit",
				"steps",
				"user-dashboard",
				"payroll-analytics",
				"hr-settings",
				"finance-settings",
				"chat-settings",
				"ticket-settings",
				"ai-settings"
			]),
		[]
	);

	// Section prefixes that indicate the first part of the URL
	const sectionPrefixes = useMemo(
		() =>
			new Set([
				"it",
				"hr",
				"system",
				"pr",
				"ec",
				"ecommerce",
				"cm",
				"radio",
				"media",
				"sys",
				"chat",
				"ticket",
				"database",
				"todo",
				"finance",
				"ai",
				"office-map"
			]),
		[]
	);

	// Helper function to get display name from API result
	const getDisplayName = useCallback((item) => {
		return item.name || item.title || item.index || item.ID || item._id;
	}, []);

	// Helper function to normalize crumb names
	const normalizeCrumbName = useCallback((crumb) => {
		if (crumb.includes("-"))
		{
			return crumb.replace(/-./g, (x) => x.toUpperCase()[1]);
		}
		return crumb;
	}, []);

	// Helper function to check if a string looks like a valid MongoDB ObjectId
	const isValidObjectId = useCallback((str) => {
		return /^[0-9a-fA-F]{24}$/.test(str);
	}, []);

	// Main function to fetch breadcrumb data
	const fetchBreadcrumbData = useCallback(
		async (crumbs) => {
			try
			{
				// Handle home route
				if (location.pathname === "/")
				{
					return ["home"];
				}

				// Filter empty crumbs
				const filteredCrumbs = crumbs.filter((crumb) => crumb !== "");

				// Office URLs are nested navigation routes (for example
				// Office pages use their own route labels, not generic CRUD entity/detail routes.
				// Let the navigation metadata provide their labels and avoid trying
				// to resolve the structural "office" segment through routeMap.
				if (filteredCrumbs[0] === "office")
				{
					return filteredCrumbs;
				}

				if (filteredCrumbs[0] === "files" && filteredCrumbs[1] === "browser")
				{
					if (filteredCrumbs[2] === "virtual-folder" && isValidObjectId(filteredCrumbs[3]))
					{
						const folderId = filteredCrumbs[3];
						const subSegments = filteredCrumbs.slice(4);
						let folderName = folderId;
						try
						{
							const { data: folders = [] } = await apiClient.get(`${filesApiBase}/virtual-folders`);
							const folder = folders.find((entry) => String(entry._id) === folderId);
							if (folder?.name)
							{
								folderName = pickLocalizedText(folder.name, lang) || folderId;
							}
						} catch (error)
						{
							console.error("Files virtual-folder breadcrumb resolve failed:", error);
						}
						return buildFilesBrowserCrumbs([
							{
								text: "All Folders",
								to: `${filesBrowserBasePath}/section/apit-folders`
							},
							{
								text: folderName,
								to: `${filesBrowserBasePath}/virtual-folder/${folderId}`
							},
							...subSegments.map((segment, index) => ({
								text: segment,
								to: `${filesBrowserBasePath}/virtual-folder/${folderId}/${encodeFilesBrowserSubPath(
									subSegments.slice(0, index + 1)
								)}`
							}))
						]);
					}

					if (filteredCrumbs[2] === "section" && filteredCrumbs[3])
					{
						const section = filteredCrumbs[3];
						const sectionLabel =
							filesBrowserSectionLabels[section] || normalizeCrumbName(section);
						return buildFilesBrowserCrumbs([
							{
								text: sectionLabel,
								to: `${filesBrowserBasePath}/section/${encodeURIComponent(section)}`
							}
						]);
					}

					return buildFilesBrowserCrumbs([{ text: "Files", to: "/files" }]);
				}

				// /chat/chat-group/:id — mobile breadcrumbs use only the last segment; resolve ID to title
				if (
					filteredCrumbs[0] === "chat" &&
					filteredCrumbs[1] === "chat-group" &&
					filteredCrumbs.length >= 3 &&
					isValidObjectId(filteredCrumbs[2])
				)
				{
					const chatGroupRoute = config.api.chat?.chatGroup;
					if (chatGroupRoute)
					{
						try
						{
							const result = await API.get({
								route: chatGroupRoute,
								query: { _id: filteredCrumbs[2] },
								limit: 1,
								populate: [{ path: "users", select: "_id name" }]
							});
							const chatDoc = result?.[0];
							if (chatDoc)
							{
								let label = "";
								if (chatDoc.chatType === "group" || chatDoc.chatType === "channel")
								{
									label = chatDoc.name || "";
								}
								if (
									!label &&
									chatDoc.chatType === "direct" &&
									Array.isArray(chatDoc.users)
								)
								{
									const selfId = user?._id ? String(user._id) : "";
									const peer =
										chatDoc.users.find((u) => u && String(u._id) !== selfId) ||
										chatDoc.users[0];
									label = pickLocalizedPersonName(peer, lang);
								}
								if (!label)
								{
									label = getDisplayName(chatDoc);
								}
								if (label && typeof label === "string")
								{
									filteredCrumbs[2] = label;
								}
							}
						} catch (error)
						{
							console.error("Chat breadcrumb resolve failed:", error);
						}
					}
					return filteredCrumbs;
				}

				// Determine entity index based on section prefixes
				let entityIndex = 0;
				if (filteredCrumbs.length > 1 && sectionPrefixes.has(filteredCrumbs[0]))
				{
					entityIndex = 1;
				}

				// Handle AI routes - similar to chat, they don't need API calls for breadcrumbs
				if (filteredCrumbs[0] === "ai")
				{
					return filteredCrumbs;
				}

				// Handle user-dashboard and payroll-analytics (frontend-only routes)
				if (["user-dashboard", "payroll-analytics"].includes(filteredCrumbs[entityIndex]))
				{
					return filteredCrumbs;
				}

				// If we don't have enough parts for an API call, return as is
				if (filteredCrumbs.length <= entityIndex + 1)
				{
					return filteredCrumbs;
				}

				// Handle users/user special case
				if (["users", "user"].includes(filteredCrumbs[entityIndex]))
				{
					filteredCrumbs[entityIndex] = "users";

					// Check if it's a static route (like user-dashboard, payroll-analytics)
					if (staticRoutes.has(filteredCrumbs[entityIndex + 1]))
					{
						return filteredCrumbs;
					}

					// Only make API call if the ID looks like a valid ObjectId
					if (!isValidObjectId(filteredCrumbs[entityIndex + 1]))
					{
						return filteredCrumbs;
					}

					// Try to fetch user data
					const apiRoute = routeMap.user;
					if (apiRoute)
					{
						const result = await API.get({
							route: apiRoute,
							query: { _id: filteredCrumbs[entityIndex + 1] },
							limit: 1
						});

						if (result?.length > 0)
						{
							filteredCrumbs[filteredCrumbs.length - 1] = getDisplayName(result[0]);
						}
					}
					return filteredCrumbs;
				}

				// Handle project special case
				if (filteredCrumbs[entityIndex] === "pr" && filteredCrumbs.length > entityIndex + 1)
				{
					// Check if it's a static route that doesn't need API call
					if (staticRoutes.has(filteredCrumbs[entityIndex + 1]))
					{
						return filteredCrumbs;
					}

					// Only make API call if the ID looks like a valid ObjectId
					if (!isValidObjectId(filteredCrumbs[entityIndex + 1]))
					{
						return filteredCrumbs;
					}

					const apiRoute = routeMap.pr;
					if (apiRoute)
					{
						const result = await API.get({
							route: apiRoute,
							query: { _id: filteredCrumbs[entityIndex + 1] },
							limit: 1
						});

						if (result?.length > 0)
						{
							filteredCrumbs[filteredCrumbs.length - 1] = getDisplayName(result[0]);
						}
					}
					return filteredCrumbs;
				}

				// Handle database/project special case
				if (
					filteredCrumbs[entityIndex] === "database" &&
					filteredCrumbs.length > entityIndex + 2 &&
					filteredCrumbs[entityIndex + 1] === "project"
				)
				{
					// Check if it's a static route that doesn't need API call
					if (staticRoutes.has(filteredCrumbs[entityIndex + 2]))
					{
						return filteredCrumbs;
					}

					// Only make API call if the ID looks like a valid ObjectId
					if (!isValidObjectId(filteredCrumbs[entityIndex + 2]))
					{
						return filteredCrumbs;
					}

					const apiRoute = routeMap.project;
					if (apiRoute)
					{
						const result = await API.get({
							route: apiRoute,
							query: { _id: filteredCrumbs[entityIndex + 2] },
							limit: 1
						});

						if (result?.length > 0)
						{
							filteredCrumbs[filteredCrumbs.length - 1] = getDisplayName(result[0]);
						}
					}
					return filteredCrumbs;
				}

				// Handle forms-specific cases
				if (filteredCrumbs[0] === "forms")
				{
					const formsApiRoute = config.api.forms?.list;
					const formsStaticSegments = new Set(["active", "create", "templates"]);
					const subSegment = filteredCrumbs[1];

					if (!subSegment || formsStaticSegments.has(subSegment))
					{
						return filteredCrumbs;
					}

					if (!formsApiRoute)
					{
						console.warn("No Forms API route found in config");
						return filteredCrumbs;
					}

					const resolveFormTitle = (formDoc) =>
						pickFormLocale(formDoc?.title, lang) || getDisplayName(formDoc);

					// /forms/answer/:id
					if (subSegment === "answer")
					{
						const formId = filteredCrumbs[2];
						if (formId && isValidObjectId(formId))
						{
							const result = await API.get({
								route: formsApiRoute,
								query: { _id: formId },
								limit: 1
							});

							if (result?.length > 0)
							{
								filteredCrumbs[2] = resolveFormTitle(result[0]);
							}
						}
						return filteredCrumbs;
					}

					// /forms/:id(/edit|preview|responses|analytics)
					if (isValidObjectId(subSegment))
					{
						const result = await API.get({
							route: formsApiRoute,
							query: { _id: subSegment },
							limit: 1
						});

						if (result?.length > 0)
						{
							filteredCrumbs[1] = resolveFormTitle(result[0]);
						}
					}

					return filteredCrumbs;
				}

				// Handle todo-specific cases
				if (filteredCrumbs[0] === "todo")
				{
					const todoRouteMap = {
						boards: config.api.todo?.board,
						board: config.api.todo?.board,
						category: config.api.todo?.category,
						task: config.api.todo?.task,
						tag: config.api.todo?.tag,
						label: config.api.todo?.label
					};

					const todoRoute = todoRouteMap[filteredCrumbs[entityIndex]];
					if (!todoRoute)
					{
						console.warn(`No Todo API route found for crumb: ${filteredCrumbs[entityIndex]}`);
						return filteredCrumbs;
					}

					const idIndex = entityIndex + 1;
					const todoEntity = filteredCrumbs[entityIndex];
					const entityIdentifier = filteredCrumbs[idIndex];
					let entityResult = [];

					if (filteredCrumbs.length > idIndex && isValidObjectId(entityIdentifier))
					{
						entityResult = await API.get({
							route: todoRoute,
							query: { _id: entityIdentifier },
							limit: 1
						});
					} else if (todoEntity === "board" && entityIdentifier)
					{
						const boards = await API.get({ route: todoRoute });
						const slugify = (value) =>
							String(value || "")
								.trim()
								.toLowerCase()
								.replace(/[^a-z0-9]+/g, "-")
								.replace(/^-+|-+$/g, "");
						const matchingBoard = (Array.isArray(boards) ? boards : []).find((board) => {
							const localizedNames =
								board?.name && typeof board.name === "object"
									? Object.values(board.name)
									: [board?.name];
							return (
								String(board?._id || "") === entityIdentifier ||
								String(board?.slug || "") === entityIdentifier ||
								localizedNames.some((name) => slugify(name) === entityIdentifier)
							);
						});
						if (matchingBoard) entityResult = [matchingBoard];
					}

					if (entityResult?.length > 0)
					{
						const entityName =
							pickLocalizedText(
								entityResult[0]?.name || entityResult[0]?.title,
								lang
							) || String(getDisplayName(entityResult[0]) || "");
						if (entityName)
						{
							// Replace at idIndex (not necessarily the last crumb) so a
							// trailing static segment like /edit still renders after it,
							// e.g. /todo/board/:id/edit -> Todo > Board > <Name> > Edit.
							filteredCrumbs[idIndex] =
								todoEntity === "board"
									? { text: entityName, to: location.pathname.replace(/\/edit$/, "") }
									: entityName;
						}
					}

					return filteredCrumbs;
				}

				// Handle security-specific cases
				if (filteredCrumbs[0] === "security")
				{
					const securityEntity = filteredCrumbs[1];
					if (!securityEntity)
					{
						return filteredCrumbs;
					}

					const securityRouteMap = {
						overview: config.api.security?.overview,
						clients: config.api.security?.clients,
						devices: config.api.security?.devices,
						statuses: config.api.security?.statuses,
						cameras: config.api.security?.cameras,
						alerts: config.api.security?.alerts,
						sessions: config.api.security?.sessions,
						media: config.api.security?.media,
						policies: config.api.security?.policies,
						events: config.api.security?.events,
						healthLogs: config.api.security?.healthLogs
					};

					const securityRoute = securityRouteMap[securityEntity];
					if (!securityRoute)
					{
						console.warn(`No Security API route found for crumb: ${securityEntity}`);
						return filteredCrumbs;
					}

					// Security detail routes are /security/{entity}/{id}
					const securityId = filteredCrumbs[2];
					if (securityId && isValidObjectId(securityId))
					{
						const result = await API.get({
							route: securityRoute,
							query: { _id: securityId },
							limit: 1
						});

						if (result?.length > 0)
						{
							filteredCrumbs[filteredCrumbs.length - 1] = getDisplayName(result[0]);
						}
					}

					return filteredCrumbs;
				}

				// Handle general case
				const normalizedCrumb = normalizeCrumbName(filteredCrumbs[entityIndex]);
				const apiRoute = routeMap[normalizedCrumb] || routeMap[filteredCrumbs[entityIndex]];

				if (!apiRoute)
				{
					console.warn(`No API route found for crumb: ${filteredCrumbs[entityIndex]}`);
					return filteredCrumbs;
				}

				// Check if we have an ID to fetch
				const idIndex = entityIndex + 2;
				if (filteredCrumbs.length > idIndex)
				{
					// Check if it's a static route that doesn't need API call
					if (staticRoutes.has(filteredCrumbs[idIndex]))
					{
						return filteredCrumbs;
					}

					// Only make API call if the ID looks like a valid ObjectId
					if (!isValidObjectId(filteredCrumbs[idIndex]))
					{
						return filteredCrumbs;
					}

					const result = await API.get({
						route: apiRoute,
						query: { _id: filteredCrumbs[idIndex] },
						limit: 1
					});

					if (result?.length > 0)
					{
						filteredCrumbs[filteredCrumbs.length - 1] = getDisplayName(result[0]);
					}
				}

				return filteredCrumbs;
			} catch (error)
			{
				console.error("Error fetching breadcrumb data:", error);
				return crumbs;
			}
		},
		[
			location.pathname,
			sectionPrefixes,
			staticRoutes,
			routeMap,
			getDisplayName,
			normalizeCrumbName,
			isValidObjectId,
			user?._id,
			lang
		]
	);

	// Effect to update breadcrumbs when location changes
	useEffect(() => {
		let isCancelled = false;

		const updateBreadcrumbs = async () => {
			setIsBusy(true);

			const pathCrumbs =
				location.pathname === "/" ? ["home"] : location.pathname.split("/").slice(1);

			const processedCrumbs = await fetchBreadcrumbData(pathCrumbs);

			if (!isCancelled)
			{
				setCrumbs(processedCrumbs);
				setIsBusy(false);
			}
		};

		updateBreadcrumbs();

		return () => {
			isCancelled = true;
			setIsBusy(false);
		};
	}, [location.pathname, fetchBreadcrumbData]);

	const navigationOptionsByPath = useMemo(() => {
		const optionsByPath = new Map();
		const visiblePlatforms = Array.isArray(platforms) ? platforms : [];
		const platformOptions = visiblePlatforms
			.map((platform) => ({
				label: navigationLabel(platform, lang),
				to: navigationRoutePath(platform.route)
			}))
			.filter((option) => option.label && option.to !== "/");

		optionsByPath.set("/home", platformOptions);

		visiblePlatforms.forEach((platform) => {
			const platformPath = navigationRoutePath(platform.route);
			optionsByPath.set(
				platformPath,
				platformOptions.filter((option) => option.to !== platformPath)
			);

			const visibleMenu = withoutPageSettingsItems(
				filterMenuItemsByReadPermission(platform.menu || [], user)
			);
			const menuOptions = visibleMenu
				.flatMap((item) => {
					const itemOption = item.route
						? [{ label: navigationLabel(item, lang), to: `/${item.route}` }]
						: [];
					const subMenuOptions = Array.isArray(item.subMenu)
						? item.subMenu
							.map((subItem) => ({
								label: navigationLabel(subItem, lang),
								to: `/${subItem.route}`
							}))
							.filter((option) => option.label && option.to !== "/undefined")
						: [];
					return [...itemOption, ...subMenuOptions];
				})
				.filter(
					(option, index, options) =>
						option.label &&
						option.to !== "/undefined" &&
						options.findIndex((candidate) => candidate.to === option.to) === index
				);

			// Some routes add a structural segment that is not a clickable sidebar item
			// (for example /hr/users). That breadcrumb still represents the whole module,
			// so expose every actionable module item from it.
			menuOptions.forEach((option) => {
				const segments = navigationRoutePath(option.to).split("/").filter(Boolean);
				for (let depth = 2; depth < segments.length; depth += 1) {
					const intermediatePath = `/${segments.slice(0, depth).join("/")}`;
					if (!optionsByPath.has(intermediatePath)) {
						optionsByPath.set(intermediatePath, menuOptions);
					}
				}
			});

			visibleMenu.forEach((item) => {
				const itemPath = navigationRoutePath(item.route);
				optionsByPath.set(
					itemPath,
					menuOptions.filter((option) => navigationRoutePath(option.to) !== itemPath)
				);

				if (!Array.isArray(item.subMenu)) return;
				const subMenuOptions = item.subMenu
					.map((subItem) => ({
						label: navigationLabel(subItem, lang),
						to: `/${subItem.route}`
					}))
					.filter((option) => option.label && option.to !== "/undefined");

				item.subMenu.forEach((subItem) => {
					const subItemPath = navigationRoutePath(subItem.route);
					optionsByPath.set(
						subItemPath,
						subMenuOptions.filter(
							(option) => navigationRoutePath(option.to) !== subItemPath
						)
					);
				});
			});
		});

		return optionsByPath;
	}, [platforms, user, lang]);

	// Memoized breadcrumb items
	const breadcrumbItems = useMemo(() => {
		const knowledgeCrumbKeys = {
			knowledge: "knowledgeBase",
			articles: "knowledgeArticles",
			categories: "knowledgeCategories",
			search: "knowledgeSearch",
			"auto-docs": "knowledgeAutoGeneratedDocs",
			settings: "settings"
		};
		const meetingCrumbKeys = {
			meetings: "meetings",
			create: "meetingsCreateMeeting"
		};
		const formsCrumbKeys = {
			forms: "formsTitle",
			active: "activeSurveys",
			create: "createForm",
			templates: "templates",
			edit: "edit",
			preview: "preview",
			responses: "responses",
			analytics: "analytics",
			answer: "answer"
		};
		return crumbs.map((crumb, index) => {
			const linkPath =
				typeof crumb === "object" && crumb?.to
					? crumb.to
					: `/${crumbs
						.slice(0, index + 1)
						.map((entry) =>
							typeof entry === "object" ? entry.text || entry[lang] || "" : entry
						)
						.filter(Boolean)
						.join("/")}`;
			const crumbText =
				typeof crumb === "object"
					? crumb.text || crumb[lang]
					: crumbs[0] === "forms" && formsCrumbKeys[crumb]
						? formsTr(formsCrumbKeys[crumb], lang)
						: T(
							knowledgeCrumbKeys[crumb] ||
							(crumbs[0] === "meetings" ? meetingCrumbKeys[crumb] : null) ||
							normalizeCrumbName(crumb),
							lang
						);
			return { key: `${linkPath}-${index}`, label: crumbText, to: linkPath };
		});
	}, [crumbs, lang, normalizeCrumbName]);

	const settingsActions = useMemo(
		() => getPageSettingsActions({
			pathname: location.pathname,
			platforms: [...(platforms || []), ...(config.headerMenu || [])],
			user
		}),
		[location.pathname, platforms, user]
	);

	if (isBusy)
	{
		return (
			<Box
				mb={"xs"}
				sx={{ display: "flex", alignItems: "center", gap: 8, minHeight: 36, width: "100%" }}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
					<Loader size="xs" />
					<Skeleton height={14} width={72} radius={8} />
					<IconChevronRight size={14} stroke={1.5} opacity={0.5} />
					<Skeleton height={14} width={120} radius={8} />
				</Box>
				<Box pr="md" sx={{ flexShrink: 0 }}>
					<PageSettingsAction actions={settingsActions} lang={lang} />
				</Box>
			</Box>
		);
	}

	const breadcrumbsWithoutPlatform = breadcrumbItems.filter((item, index) => {
		if (index !== 0) return true;
		return !(Array.isArray(platforms) ? platforms : []).some(
			(platform) => navigationRoutePath(platform.route) === navigationRoutePath(item.to)
		);
	});
	const itemsToRender = isMobile
		? breadcrumbsWithoutPlatform.slice(-1)
		: breadcrumbsWithoutPlatform;
	const homeItem = (
		<BreadcrumbPill
			key="breadcrumb-home"
			home
			to="/home"
			alternatives={navigationOptionsByPath.get("/home") || []}
			isMobile={isMobile}
		/>
	);

	return (
		<Box
			mb={10}
			sx={{ display: "flex", alignItems: "flex-start", gap: 8, width: "100%", minWidth: 0 }}
		>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Breadcrumbs
					separator={<IconChevronRight size={16} stroke={1.8} />}
					styles={(theme) => ({
						root: {
							flexWrap: "wrap",
							rowGap: theme.spacing.sm,
							overflowX: "auto",
							width: "100%",
							maxWidth: "100%",
							boxSizing: "border-box",
							padding: `2px 0 ${theme.spacing.xs}px ${theme.spacing.md}px`
						},
						separator: {
							marginLeft: theme.spacing.xs,
							marginRight: theme.spacing.xs,
							opacity: colorScheme === "dark" ? 0.55 : 0.4
						}
					})}
				>
					{homeItem}
					{itemsToRender.map((item, index) => (
						<BreadcrumbPill
							key={item.key}
							label={item.label}
							to={item.to}
							alternatives={navigationOptionsByPath.get(navigationRoutePath(item.to)) || []}
							active={index === itemsToRender.length - 1}
							isMobile={isMobile}
						/>
					))}
				</Breadcrumbs>
			</Box>
			<Box pt={2} pr="md" sx={{ flexShrink: 0 }}>
				<PageSettingsAction actions={settingsActions} lang={lang} />
			</Box>
		</Box>
	);
}
